/**
 * A right-hand panel over the page: lead 360, Explain, a product, an outcome form.
 *
 * Radix Dialog does the hard parts properly — focus trap, restore on close, Escape, inert
 * background, aria-modal — which is why this is a wrapper and not a hand-rolled div.
 *
 * Author: Danish Khan
 */
import * as Dialog from '@radix-ui/react-dialog';
import clsx from 'clsx';
import type { ReactNode } from 'react';

export interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** 'md' for a form, 'lg' for lead 360 / Explain. */
  size?: 'md' | 'lg';
  footer?: ReactNode;
  children: ReactNode;
}

export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  size = 'md',
  footer,
  children,
}: DrawerProps): ReactNode {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-[var(--h1-overlay)] data-[state=open]:animate-[fade-in_120ms_ease-out]" />
        <Dialog.Content
          className={clsx(
            'fixed top-0 right-0 z-50 flex h-full w-full flex-col border-l border-line bg-surface shadow-2xl',
            size === 'lg' ? 'max-w-2xl' : 'max-w-md',
          )}
        >
          <header className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
            <div className="min-w-0">
              <Dialog.Title className="truncate text-base font-semibold text-fg">{title}</Dialog.Title>
              {description ? (
                <Dialog.Description className="mt-0.5 text-sm text-fg-muted">{description}</Dialog.Description>
              ) : (
                <Dialog.Description className="sr-only">{title}</Dialog.Description>
              )}
            </div>
            <Dialog.Close
              className="rounded-md px-2 py-1 text-sm text-fg-muted hover:bg-surface-2 hover:text-fg"
              aria-label="Close"
            >
              ✕
            </Dialog.Close>
          </header>
          <div className="h1-scroll min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && <footer className="border-t border-line px-5 py-3">{footer}</footer>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
